import { type PhoneResult, phone as parsePhoneLib } from 'phone'

/**
 * Parses a phone number using the phone library
 * @param phone - The phone number to parse
 * @returns Phone parsing result with isValid, phoneNumber, and countryCode
 */
export function parsePhone(phone: string): PhoneResult {
  return parsePhoneLib(phone)
}

/**
 * Normalizes a phone number by removing country code and formatting consistently
 * @param phone - The phone number to normalize
 * @returns A normalized phone number string or empty string if invalid
 */
/**
 * Generates comma-separated phone number variants for FHIR search.
 * Medplum does not normalize phone numbers on lookup — it matches the stored
 * value exactly. While we normalize what we ingest, not every entrypoint of
 * data into our store is under our control (e.g. Medplum bots, third-party
 * integrations, manual edits), so we search across all common US formats.
 */
export function phoneSearchVariants(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  const bare =
    digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits

  if (bare.length !== 10) {
    return phone
  }

  const area = bare.slice(0, 3)
  const prefix = bare.slice(3, 6)
  const line = bare.slice(6)

  return [
    `+1${bare}`,
    `1${bare}`,
    bare,
    `(${area}) ${prefix}-${line}`,
    `${area}-${prefix}-${line}`,
    `${area} ${prefix} ${line}`,
    `${area}.${prefix}.${line}`,
  ].join(',')
}

// Fax numbers share the same US phone-number format, so this delegates to
// the same validator rather than duplicating format rules.
export function isValidFaxNumber(value: string): boolean {
  return parsePhone(value).isValid
}

export const normalizePhone = (phone: string | undefined): string => {
  if (!phone) {
    return ''
  }

  const result = parsePhone(phone)
  if (!result.isValid) {
    return ''
  }

  if (result.countryCode) {
    return result.phoneNumber.replace(
      new RegExp(`^(\\+?${result.countryCode.replace('+', '')})`),
      ''
    )
  }

  return result.phoneNumber
}
